import { DateRange } from "@mui/icons-material";
import { FormLabel, Button, AutocompleteChangeDetails, AutocompleteChangeReason, Box, useTheme, Link } from "@mui/material";
import _ from "lodash";
import React, { forwardRef, Ref, useEffect, useImperativeHandle} from "react";
import { useTranslation } from "react-i18next";
import { AccessFunctionalityTypeEnum } from "../../common/enums/AccessFunctionalityTypeEnum";
import { AccessTypeEnum } from "../../common/enums/AccessTypeEnum";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { ActionTypeEnum, StatusEnum } from "../../common/IExportNotificationMsg";
import { hasPermission } from "../../common/utilities/HasPermission";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useProgressContext } from "../../contexts/progress-context/ProgressContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useCustomFormInstance } from "../../queries/custom-form-instances/UseCustomFormInstances";
import { useDeleteCustomFormInstance } from "../../queries/custom-form-instances/UseDeleteCustomFormInstance";
import { useCustomFormTemplate } from "../../queries/custom-form-templates/UseCustomFormTemplates";
import { useObjectViewState } from "../../queries/object-view-state/UseObjectViewState";
import { useDRCXAvailableObjects } from "../../queries/drcx/UseDRCXAvailableObjects";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import CustomFormsDataService from "../../services/custom-forms-data-service/CustomFormsDataService";
import { IFormTemplate } from "../../services/custom-forms-data-service/TemplateModels";
import { IFormInstanceData } from "../../views/object-form-view/Models";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ContentContainer } from "../content-container/ContentContainer";
import { ContentSection } from "../content-section/ContentSection";
import { ViewOptionStateEnum } from "../contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialog, CustomDurationDialogValues, useCustomDurationDialogState } from "../custom-duration-dialog/CustomDurationDialog";
import CustomFormRunnerDialog from "../custom-form-runner-dialog/CustomFormRunnerDialog";
import GroupedVirtualizedList from "../grouped-virtualized-list/GroupedVirtualizedList";
import LoadingZone from "../loading-zone/LoadingZone";
import NoData from "../no-data/NoData";
import OptionsSideSheet from "../options-side-sheet/OptionsSideSheet";
import SideSheetMultiSelect from "../side-sheet/side-sheet-multiselect/SideSheetMultiSelect";
import { CustomFormListActionEnum } from "./CustomFormInstanceListActions";
import useCustomFormInstanceListState from "./CustomFormInstanceListState";
import CustomFormInstanceListUtils, { ALL_FORM_TEMPLATES_ID, FormInstanceItem } from "./CustomFormInstanceListUtils";
import { CustomFormInstanceActionEnum, CustomFormInstanceActionPayloadType, CustomFormInstanceListItem } from "./CustomFormInstancetListItem";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import UUID from "../../common/utilities/Uuid";
import { RestError } from "../../services/rest-service/RestService";
import { bindDialog } from "../../hooks/use-dialog-state/UseDialogState";



export type CustomFormInstanceListProps = {
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
    setActions?: (actions: ActionMenuItem[]) => void;
};

export type CustomFormInstanceListType = {
    executeAction: (action: ActionMenuItem) => void;
};

function CustomFormInstanceList({
    setViewOptionState,
    setActions
}: CustomFormInstanceListProps, ref: Ref<unknown> | undefined) {    

    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();
    const { confirm, acknowledge } = useConfirmation();
    const [,setAppNotificationState] = useAppNotificationContext();    

    const [progressState, actionStarted] = useProgressContext();

    const durationDialog = useCustomDurationDialogState();

    const theme = useTheme();

    const {
        state,
        setMode,
        setTemplates,
        setDuration,
        setFormRunnerOpen,
        setTemplateToRun,
        setAvailableObjects,
        setInstanceData,
        setDownloadUid,
        setShowNoTemplateDialog,
        resetState
    } = useCustomFormInstanceListState();
    
    useImperativeHandle<unknown, CustomFormInstanceListType>(
        ref,
        () => ({
            executeAction: executeAction
        })
    );

    const { data: templates, status } = useQueryNotifications(useCustomFormTemplate)({
        customerID: userState.customer?.id as number,
        routeContext: routeContext,
        offlineDataLoaded: offlineState.offlineDataLoaded,
        offline: offlineState.offline,
        //onSuccess: ((data: IFormTemplate[] | undefined) => {
        //
        //})
    });  

    const { data: objectViewState } = useObjectViewState({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject,
        offline: offlineState.offline
    });

    const objectName = objectViewState?.contextObject.objectName ?? "";

    const formInstanceModify = hasPermission(objectViewState?.accessPermissions ?? [], { functionalityType: AccessFunctionalityTypeEnum.FormInstance, access: AccessTypeEnum.Write_Change});

    const { data, status: getStatus, refetch } = useQueryNotifications(useCustomFormInstance)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        offline: offlineState.offline,
        startTime: state.durationOptions.duration.startDate,
        endTime: state.durationOptions.duration.endDate
        //sessionToken: state.sessionToken
    });

    
    const { mutate: deleteFormInstance } = useQueryNotifications(useDeleteCustomFormInstance)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        objectID: routeContext.contextObject?.id as number,
        objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
        offline: offlineState.offline,
        startTime: state.durationOptions.duration.startDate,
        endTime: state.durationOptions.duration.endDate,
        onSuccess: () => {
            acknowledge(t('deleteSuccessMessage', {itemName: t('dataTypeNames.forms')}))
        }
    });

    const { data: availableObjects, status: availableObjectStatus } = useQueryNotifications(useDRCXAvailableObjects)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        includeDescendentGroups: false,
        includeTankBatteries: true,
        offline: offlineState.offline,
        features: ObjectFeaturesEnum.FormModify,
        enabled: routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group
    });

    const instanceItems = CustomFormInstanceListUtils.getInstanceItems(data, templates, state.filteredTemplates);



    function onTemplateChanged(
        event: React.SyntheticEvent,
        value: IFormTemplate[],
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<IFormTemplate> | undefined
    ) {
        const items = [...state.templates];

        let item = items.find(o => o.id === details?.option.id);

        if (item) {
            switch (reason) {
                case 'selectOption':
                    item.selected = true;

                    if (item.id === ALL_FORM_TEMPLATES_ID) {
                        items.forEach(o => {
                            if (o.id !== item?.id) {
                                o.selected = false;
                            }
                        });
                    } else {

                        items.filter(o => o.id === ALL_FORM_TEMPLATES_ID)
                            .forEach(o => o.selected = false);
                    }

                    break;


                case 'removeOption':
                    item.selected = false;

                    if (!items.find(o => o.selected)) {
                        item = items.find(o => o.id === ALL_FORM_TEMPLATES_ID);
                        if (item) {
                            item.selected = true;
                        }
                    }
                    break;

                default:
                    break;
            }
        }
        setTemplates(items);
    }

    const onCustomDuration = async () => {
        const result = await durationDialog.open(state.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    };

    async function getDeleteFormInstanceConfirmation() {

        return (await confirm({
            dialogTitle: t("deleteConfirmation.title", { itemName: t("dataTypeNames.forms") }),
            confirmationText: t("deleteConfirmation.confirmationText"),
            yesButtonText: t("deleteConfirmation.yesButtonText"),
            noButtonText: t("deleteConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }



    async function confirmAndDeleteFormInstance(item: IFormInstanceData) {
        const canDelete = await getDeleteFormInstanceConfirmation();

        if (canDelete) {
            deleteFormInstance(item);
        }
    }

    async function onAction(action: CustomFormInstanceActionEnum, payload: CustomFormInstanceActionPayloadType) {

        const item = payload as FormInstanceItem;
        
        if (action === CustomFormInstanceActionEnum.Delete) {
            confirmAndDeleteFormInstance(item)

        } else {
            setInstanceData(item as IFormInstanceData);        
            setMode(action === CustomFormInstanceActionEnum.ViewForm ? 'view' : 'edit');
            setTemplateToRun(item.model);
            setFormRunnerOpen(true);
        }
    }

    function renderItem(index: number, _groupKey: number, item: FormInstanceItem) {
        return (
            getStatus === 'success' && item.nodata ? 
                <Box sx={{margin: theme.spacing(.5)}}>
                    <NoData text={t(item.groupKey === 0 ? "customForms.noOpenData" : "customForms.noCloseData", {name: objectName})} />
                </Box> :
                <CustomFormInstanceListItem
                    key={`${index}`}
                    item={item}
                    onAction={onAction}
                    expanded={false}
                    allowEdit={formInstanceModify}
                />
        );
    }


    function renderGroup(groupKey: number) {
        const groupText = groupKey === 0 ? 'Open Forms' : 'Closed Forms';
        return (
            <Box sx={{
                    backgroundColor: "custom.background.1",
                    height: theme => theme.spacing(6),
                    width: 1,
                    paddingLeft: 1,
                    display: "flex",
                    alignItems: "center"
                }}>
                {groupText}
            </Box>
        );
    }


    const noDataMessage = data && instanceItems.length === 0
        ? t("noData.noDataTypesForObject", { dataTypeName: t("dataTypeNames.forms"), objectName })
            : undefined;

    const onExport = (mimeType: ApplicationMimeTypesEnum) => {

        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.contextObject?.id as number,
            objectType: routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
            title: objectName, 
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });
        
        try {

            CustomFormsDataService.export(routeContext.groupID as number, 
                routeContext.contextObject?.id as number,
                routeContext.contextObject?.objectType as eLynxObjectTypeEnum,
                state.durationOptions.duration.startDate as Date,
                state.durationOptions.duration.endDate ?? new Date(),
                uid,
                mimeType);


            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export); 
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions && setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });

        }
    }

    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.contextObject?.id && o.objectType === routeContext.contextObject?.objectType);
        

    function executeAction(item: ActionMenuItem) {


        switch (item.action) {
            case BaseActionEnum.Refresh:
                refetch();
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel2007);
                break;

            default:

                // check for subaction executed
                const template = templates?.find(o => !o.isDeleted && o.name === item.action as string);
                if (template) {
                    const objects = routeContext.contextObject?.objectType === eLynxObjectTypeEnum.Group && availableObjects ? 
                                        CustomFormInstanceListUtils.filterAvailableObjects(template, availableObjects) : 
                                        undefined;
                    setTemplateToRun(template);                    
                    setFormRunnerOpen(true);
                    setAvailableObjects(objects);
                }
                break;

        }
    }

    useEffect(() => {

        const showNoFormTemplatesDialog = async () => {

            setShowNoTemplateDialog(true);

            await confirm({
                dialogTitle: t("customForms.welcome"),
                yesButtonText: t("buttons.close"),
                showConfirmationText: false,
                confirmationBodyComponent: 
                <Box>
                    {t('customForms.welcomeText1')}
                    <Link sx={{
                        marginTop: 'auto',
                        marginBottom: 'auto', 
                        // marginLeft: '1em', 
                        // marginRight: '1em',
                        fontWeight: 600
                        }}
                        aria-label="learn about eLynx forms" 
                        href="https://support.elynxtech.com/hc/en-us/articles/4642137549851-Custom-Forms" 
                        target="_blank">
                            here
                    </Link>
                    {t('customForms.welcomeText2')}
                </Box>,
                showYesButton: true,
                showNoButton: false,
                showCloseButton: true
            });
        }

        if (templates && templates.length > 0) {
            const items = CustomFormInstanceListUtils.getFormTemplates(templates);

            if (!_.isEqual(state.templates.map(o => 
                {
                    return {
                        id: o.id, 
                        name: o.name
                    }
                }), 
                items.map(o => {
                    return {
                        id: o.id, 
                        name: o.name
                    }
                }
                ))) {
                setTemplates(items);
            }
        } else if (status === 'success' && !state.showNoTemplateDialog) {
            showNoFormTemplatesDialog();
        }
    }, [
        templates,
        state.templates,
        setTemplates,
        t,
        confirm,
        state.showNoTemplateDialog,
        setShowNoTemplateDialog,
        status
    ]);

    useEffect(() => {
        const viewOptionState = state.isDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
        setViewOptionState && setViewOptionState(viewOptionState);
    }, [
        state.isDirty,
        setViewOptionState
    ]);


    useEffect(() => {
        let actions = _.cloneDeep(state.actions);

        const templateList = templates?.filter(o => !o.isDeleted) ?? [];

        if (!formInstanceModify || (templateList && templateList.length === 0)) {
            actions = actions.filter(o => o.id !== CustomFormListActionEnum.AddFormData);
        }

        const exportAction = actions.find(o => o.action === BaseActionEnum.Export); 

        if (exportAction) {
            exportAction.disabled = (progress && progress.uid === state.downloadUid) ||
                                    (templateList && templateList.length === 0);
        }

        if (!_.isEqual(state.actions, actions)) {
            setActions && setActions(actions);
        }
    }, [
        progress,        
        state.actions,
        state.downloadUid,
        setActions,
        formInstanceModify,
        templates
    ]);

    return (
            <>
                <LoadingZone isLoading={getStatus === "loading" || availableObjectStatus === 'loading'}>
                    <ContentContainer>
                        <ContentSection>
                            {getStatus === "success" && noDataMessage &&
                                <NoData text={noDataMessage} />
                            }

                            {instanceItems && instanceItems.length > 0  ? <GroupedVirtualizedList
                                items={instanceItems}
                                renderItem={renderItem}
                                renderGroup={renderGroup}

                            /> : null}
                        </ContentSection>

                        <OptionsSideSheet onReset={resetState}>

                            <SideSheetMultiSelect<IFormTemplate>
                                id="filter-form-templates"
                                label={t('forms.filterTemplates')}
                                placeholder='Form Templates'
                                options={state.templates}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                value={state.templates.filter(o => o.selected)}
                                getOptionLabel={(option) => option.name}
                                onChange={onTemplateChanged}
                                disablePortal={true}
                            />


                            <FormLabel component="legend">
                                {t('customDuration.duration')}
                            </FormLabel>
                            <Button variant='text' 
                                startIcon={<DateRange/>} 
                                sx={{justifyContent: 'flex-start'}}
                                onClick={onCustomDuration}>
                                    {state.durationLabel}
                            </Button>                                

                        </OptionsSideSheet>


                    </ContentContainer>

                    {state.templateToRun && 
                        <CustomFormRunnerDialog 
                            mode={state.mode}
                            open={state.formRunnerOpen} 
                            instanceData={state.instanceData}
                            availableObjects={state.availableObjects}
                            startTime={state.durationOptions.duration.startDate}
                            endTime={state.durationOptions.duration.endDate}
                            onClose={() => {
                                setMode('add');
                                setTemplateToRun(undefined);
                                setInstanceData(undefined);
                                setAvailableObjects(undefined);
                                setFormRunnerOpen(false);
                                refetch();
                            }}
                            model={state.templateToRun} />
                    }

                </LoadingZone>

                <CustomDurationDialog
                    //overrideDefaultText={t('customDuration.30Days')}
                    hideDefault={true}
                    value={state.durationOptions}
                    {...bindDialog(durationDialog)}
                />  
        </>
    );
}

export default forwardRef(CustomFormInstanceList);